import React, { useState } from 'react';
import './ApiWebhooks.css';

const ApiWebhooks = () => {
    const [activeExample, setActiveExample] = useState('flask');

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
    };

    const flaskExample = `from flask import Flask, request, jsonify
import logging

app = Flask(__name__)

@app.route('/webhooks/bookwitch', methods=['POST'])
def handle_bookwitch_callback():
    try:
        data = request.get_json()
        
        guid = data.get('guid')
        status = data.get('status')
        
        if status == 'IN_PROGRESS':
            print(f"📚 Generation started for {guid}")
            
        elif status == 'DONE':
            title = data.get('title')
            print(f"✅ Book completed: {title}")
            # Notify user, update DB, etc.
            
        elif status == 'ERROR':
            error = data.get('error')
            print(f"❌ Error for {guid}: {error}")
            # Handle error, notify user, etc.
            
        return jsonify({"status": "received"}), 200
        
    except Exception as e:
        return jsonify({"error": str(e)}), 500`;

    const expressExample = `const express = require('express');
const app = express();

app.use(express.json());

app.post('/webhooks/bookwitch', (req, res) => {
    try:
        const { guid, status, title, error, message } = req.body;
        
        switch(status) {
            case 'IN_PROGRESS':
                console.log(\`📚 Generation started for \${guid}\`);
                break;
                
            case 'DONE':
                console.log(\`✅ Book completed: \${title}\`);
                // Success processing
                break;
                
            case 'ERROR':
                console.log(\`❌ Error for \${guid}: \${error}\`);
                // Error processing
                break;
        }
        
        res.status(200).json({ status: 'received' });
        
    } catch (error) {
        res.status(500).json({ error: error.message });
    }
});`;

    const CodeBlock = ({ code, language = 'python' }) => (
        <div className="code-block">
            <div className="code-header">
                <span className="language-label">{language}</span>
                <button 
                    className="copy-button"
                    onClick={() => copyToClipboard(code)}
                    title="Copy to clipboard"
                >
                    📋 Copy
                </button>
            </div>
            <pre><code>{code}</code></pre>
        </div>
    );

    const JsonBlock = ({ json }) => (
        <div className="code-block">
            <div className="code-header">
                <span className="language-label">json</span>
            </div>
            <pre><code>{JSON.stringify(json, null, 2)}</code></pre>
        </div>
    );

    return (
        <div className="api-webhooks-container">
            <div className="api-webhooks-content">
                <div className="hero-section">
                    <h1>🔔 BookWitch API Webhooks</h1>
                    <p className="hero-description">
                        Receive real-time notifications about your book generation progress. 
                        No more polling - get instant updates when your books are ready.
                    </p>
                </div>

                <div className="overview-section">
                    <h2>How Webhooks Work</h2>
                    <div className="workflow-steps">
                        <div className="workflow-step">
                            <div className="step-number">1</div>
                            <div className="step-content">
                                <h3>Create Task with Callback</h3>
                                <p>Provide a <code className="inline-code">callback_url</code> when creating a generation task</p>
                            </div>
                        </div>
                        <div className="workflow-step">
                            <div className="step-number">2</div>
                            <div className="step-content">
                                <h3>Automatic Notifications</h3>
                                <p>Receive POST requests to your URL on each status change</p>
                            </div>
                        </div>
                        <div className="workflow-step">
                            <div className="step-number">3</div>
                            <div className="step-content">
                                <h3>Process & Respond</h3>
                                <p>Handle the data and return a 200 status code to confirm receipt</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>🎯 Webhook Requirements</h2>
                    <div className="requirements-grid">
                        <div className="requirement-item success">
                            <span className="requirement-icon">✅</span>
                            <div>
                                <strong>Publicly accessible URL</strong>
                                <small>HTTPS recommended for security</small>
                            </div>
                        </div>
                        <div className="requirement-item success">
                            <span className="requirement-icon">✅</span>
                            <div>
                                <strong>Accepts POST requests</strong>
                                <small>Content-Type: application/json</small>
                            </div>
                        </div>
                        <div className="requirement-item success">
                            <span className="requirement-icon">✅</span>
                            <div>
                                <strong>Returns 200-299 status</strong>
                                <small>To confirm successful receipt</small>
                            </div>
                        </div>
                        <div className="requirement-item warning">
                            <span className="requirement-icon">⚠️</span>
                            <div>
                                <strong>10 seconds timeout maximum</strong>
                                <small>Keep processing fast</small>
                            </div>
                        </div>
                        <div className="requirement-item error">
                            <span className="requirement-icon">❌</span>
                            <div>
                                <strong>No automatic retry</strong>
                                <small>Handle failures gracefully</small>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>📡 Webhook Events</h2>
                    <div className="events-grid">
                        <div className="event-card">
                            <div className="event-header">
                                <span className="event-icon">📍</span>
                                <h3>IN_PROGRESS</h3>
                            </div>
                            <p>Sent when book generation starts</p>
                            <JsonBlock json={{
                                "guid": "abc123def456",
                                "status": "IN_PROGRESS",
                                "message": "Generation started"
                            }} />
                        </div>

                        <div className="event-card">
                            <div className="event-header">
                                <span className="event-icon">✅</span>
                                <h3>DONE</h3>
                            </div>
                            <p>Sent when generation completes successfully</p>
                            <JsonBlock json={{
                                "guid": "abc123def456",
                                "status": "DONE",
                                "title": "Artificial Intelligence: Complete Guide",
                                "message": "Generation completed successfully"
                            }} />
                        </div>

                        <div className="event-card">
                            <div className="event-header">
                                <span className="event-icon">❌</span>
                                <h3>ERROR</h3>
                            </div>
                            <p>Sent when generation fails</p>
                            <JsonBlock json={{
                                "guid": "abc123def456",
                                "status": "ERROR",
                                "error": "OpenAI API error",
                                "message": "API rate limit exceeded"
                            }} />
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>💻 Implementation Examples</h2>
                    
                    <div className="tab-container">
                        <div className="tab-buttons">
                            <button 
                                className={`tab-button ${activeExample === 'flask' ? 'active' : ''}`}
                                onClick={() => setActiveExample('flask')}
                            >
                                🐍 Python/Flask
                            </button>
                            <button 
                                className={`tab-button ${activeExample === 'express' ? 'active' : ''}`}
                                onClick={() => setActiveExample('express')}
                            >
                                🟨 Node.js/Express
                            </button>
                        </div>
                        
                        <div className="tab-content">
                            {activeExample === 'flask' && <CodeBlock code={flaskExample} language="python" />}
                            {activeExample === 'express' && <CodeBlock code={expressExample} language="javascript" />}
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>🔒 Security Best Practices</h2>
                    <div className="best-practices">
                        <div className="practice-item">
                            <h4>🔍 Validate Payload</h4>
                            <p>Always check that required fields are present in the webhook data.</p>
                            <CodeBlock code={`def is_valid_callback(data):
    # Check that required fields are present
    required_fields = ['guid', 'status', 'message']
    return all(field in data for field in required_fields)`} language="python" />
                        </div>

                        <div className="practice-item">
                            <h4>🔁 Handle Idempotence</h4>
                            <p>Your endpoint may receive the same webhook multiple times. Make sure your processing is idempotent.</p>
                            <CodeBlock code={`def handle_callback_idempotent(guid, status):
    # Check if this event has already been processed
    if already_processed(guid, status):
        return
    
    # Process the event
    process_event(guid, status)
    
    # Mark as processed
    mark_as_processed(guid, status)`} language="python" />
                        </div>

                        <div className="practice-item">
                            <h4>⚠️ Error Handling</h4>
                            <p>Handle errors gracefully and always return a proper HTTP status code.</p>
                            <CodeBlock code={`@app.route('/webhooks/bookwitch', methods=['POST'])
def handle_callback():
    try:
        data = request.get_json()
        
        # Validation
        if not is_valid_callback(data):
            return jsonify({"error": "Invalid callback data"}), 400
        
        # Processing
        process_callback(data)
        
        # Success
        return jsonify({"status": "received"}), 200
        
    except Exception as e:
        # Log error but return 200 to avoid retries
        logger.error(f"Callback processing error: {e}")
        return jsonify({"status": "error_logged"}), 200`} language="python" />
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>🧪 Testing Your Webhook</h2>
                    <div className="testing-options">
                        <div className="testing-option">
                            <h4>🌐 With ngrok (Local Development)</h4>
                            <p>Expose your local development server to the internet for testing.</p>
                            <CodeBlock code={`# Terminal 1: Run your application
python app.py

# Terminal 2: Expose publicly
ngrok http 5000

# Use the ngrok URL as callback_url
# Example: https://abc123.ngrok.io/webhooks/bookwitch`} language="bash" />
                        </div>

                        <div className="testing-option">
                            <h4>🔗 With Webhook Testing Services</h4>
                            <div className="testing-services">
                                <a href="https://webhook.site" target="_blank" rel="noopener noreferrer" className="service-link">
                                    🌐 webhook.site
                                    <small>Get a temporary URL for testing</small>
                                </a>
                                <a href="https://requestbin.com" target="_blank" rel="noopener noreferrer" className="service-link">
                                    📦 requestbin.com
                                    <small>Inspect received requests</small>
                                </a>
                                <a href="https://postbin.org" target="_blank" rel="noopener noreferrer" className="service-link">
                                    📮 postbin.org
                                    <small>Alternative webhook capture service</small>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="section">
                    <h2>❓ Common Error Types</h2>
                    <div className="error-types">
                        <div className="error-type">
                            <strong>OpenAI API error</strong>
                            <p>Issues with OpenAI API (rate limit, invalid key, etc.)</p>
                        </div>
                        <div className="error-type">
                            <strong>Invalid response format</strong>
                            <p>AI response not conforming to expected format</p>
                        </div>
                        <div className="error-type">
                            <strong>Database error</strong>
                            <p>Database connection or query issues</p>
                        </div>
                        <div className="error-type">
                            <strong>Internal server error</strong>
                            <p>Unexpected server-side errors</p>
                        </div>
                    </div>
                </div>

                <div className="faq-section">
                    <h2>🙋‍♀️ Frequently Asked Questions</h2>
                    <div className="faq-items">
                        <div className="faq-item">
                            <h4>Q: What happens if my endpoint is unavailable?</h4>
                            <p>A: The webhook will be marked as failed. There's no automatic retry, so you'll need to poll the API to check task status.</p>
                        </div>
                        <div className="faq-item">
                            <h4>Q: Can I use the same callback URL for multiple tasks?</h4>
                            <p>A: Yes! Use the <code className="inline-code">guid</code> field to identify each specific task.</p>
                        </div>
                        <div className="faq-item">
                            <h4>Q: Are webhooks guaranteed to arrive in order?</h4>
                            <p>A: No, treat each webhook independently based on the received status.</p>
                        </div>
                        <div className="faq-item">
                            <h4>Q: Is there a limit on the callback URL length?</h4>
                            <p>A: URLs must be less than 2048 characters and be valid HTTP/HTTPS URLs.</p>
                        </div>
                    </div>
                </div>

                <div className="help-section">
                    <h2>🆘 Need Help?</h2>
                    <div className="help-links">
                        <a href="/api/quickstart" className="help-link">
                            🚀 Quick Start Guide
                        </a>
                        <a href="https://api.bookwitch.io/apidocs/" className="help-link" target="_blank" rel="noopener noreferrer">
                            📚 Full API Documentation
                        </a>
                        <a href="/contact" className="help-link">
                            💌 Contact Support
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApiWebhooks; 